import { useCallback, useEffect, useMemo, useState } from 'react';
import { Wrench } from 'lucide-react';
import { Hint } from '@/components/Hint';
import { statusPillClass } from '@/lib/status';
import {
  createOrderTask,
  type OpenAgentTask,
  type OrderSummary,
} from '@/api/orders';
import { getSetupFolders, type SetupFolderSummary } from '@/api/orderPrep';
import { Can } from '@/auth/guards';
import { dueTone, fmtDate, setupBreakdown } from './lib';
import {
  JourneyName, NewSupplierLink, PendingNudge, RowAction,
  WorkEmpty, WorkNotice, WorkRow, WorkSection,
} from './workLib';
import { todayDateString } from './workUtils';
import { SetupFolderChip, SetupFolderPanel } from './SetupFolderSection';

type SetupGroup = 'ready' | 'building' | 'submitted' | 'none';

const GROUP_TITLES: Record<SetupGroup, string> = {
  ready: 'Ready to submit',
  building: 'Being built',
  submitted: 'Submitted, checking the catalog',
  none: 'Needs setup, no folder yet',
};

function groupOf(folder: SetupFolderSummary | undefined): SetupGroup {
  if (!folder) return 'none';
  if (folder.status === 'ready') return 'ready';
  if (folder.status === 'submitted') return 'submitted';
  return 'building';
}

/**
 * The setup queue: journeys with products not in the catalog yet — plus
 * journeys needing a supplier account, whatever their stage (a new supplier
 * can surface on-order or in transit, so To order alone can't carry the flag).
 * Grouped by how far the setup work has got (ready first), oldest street date first. needsSetup is DERIVED — the
 * server cannot re-check the catalog — so "Mark set up" records a verify_setup
 * nudge and the tag honestly reads "verifying" until the next run stamps real
 * pids/cat_ids (and, for suppliers, the supplier_id), at which point it clears
 * for real (or comes back).
 *
 * Joined by journey id: the setup-pending workspace mirror (setup_folders),
 * which adds the dimension the flags can't carry — whether the setup work is
 * already BUILT and waiting on submission, and the products.json itself.
 */
export function SetupView({ orders, tasks, onChanged, onOpenJourney, initialFolder }: {
  orders: OrderSummary[];
  tasks: OpenAgentTask[];
  onChanged: () => void;
  onOpenJourney: (orderId: string) => void;
  /** From /setup/:folder — opens that folder's panel on arrival. */
  initialFolder?: string | null;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);

  // The synced setup-pending workspace, joined onto rows by journey id.
  // null = not loaded yet, so rows don't flash "no folder" before the fetch.
  const [folders, setFolders] = useState<SetupFolderSummary[] | null>(null);
  const [parkedFolders, setParkedFolders] = useState<SetupFolderSummary[]>([]);
  const [showParked, setShowParked] = useState(false);
  const [foldersError, setFoldersError] = useState<string | null>(null);
  const [openFolders, setOpenFolders] = useState<Set<string>>(
    () => new Set(initialFolder ? [initialFolder] : [])
  );

  const loadFolders = useCallback(async () => {
    try {
      const [live, parked] = await Promise.all([getSetupFolders(), getSetupFolders({ status: 'parked' })]);
      setFolders(live);
      setParkedFolders(parked);
      setFoldersError(null);
    } catch (e) {
      setFoldersError(e instanceof Error ? e.message : 'Failed to load setup folders');
    }
  }, []);

  useEffect(() => { loadFolders(); }, [loadFolders]);

  const toggleFolder = useCallback((folder: string) => {
    setOpenFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folder)) next.delete(folder);
      else next.add(folder);
      return next;
    });
  }, []);

  const verifyTaskByOrder = useMemo(() => {
    const map = new Map<string, OpenAgentTask>();
    for (const t of tasks) if (t.kind === 'verify_setup') map.set(t.orderId, t);
    return map;
  }, [tasks]);

  const folderByOrder = useMemo(() => {
    const map = new Map<string, SetupFolderSummary>();
    for (const f of folders ?? []) if (f.orderId) map.set(f.orderId, f);
    return map;
  }, [folders]);

  // A journey whose only live folders are parked is dead setup work: out of
  // the list (and the sidebar count, server-side) until it is unparked.
  const parkedOrderIds = useMemo(() => {
    const ids = new Set<string>();
    for (const f of parkedFolders) if (f.orderId && !folderByOrder.has(f.orderId)) ids.add(f.orderId);
    return ids;
  }, [parkedFolders, folderByOrder]);

  const rows = useMemo(
    () => orders.filter((o) => !parkedOrderIds.has(o.id)),
    [orders, parkedOrderIds]
  );

  // Folders whose journey is NOT on the needs-setup list — a built folder
  // whose flags already cleared, an orphan (journey id null), or a batch
  // folder. They must not hide just because the flag is gone: until the
  // products are submitted, the work still exists.
  const unmatchedFolders = useMemo(() => {
    if (!folders) return [];
    const listed = new Set(rows.map((o) => o.id));
    return folders.filter((f) => !f.orderId || !listed.has(f.orderId));
  }, [folders, rows]);

  /**
   * Ready folders first (built, waiting only on the submit), then the ones
   * being built, then submitted ones the run is verifying, then journeys with
   * no folder yet. Inside a group, the oldest street date (else ship date)
   * leads — the longest-overdue submit is the top of the page.
   */
  const groups = useMemo(() => {
    type Item = { key: string; date: string; order?: OrderSummary; folder?: SetupFolderSummary };
    const out: Record<SetupGroup, Item[]> = { ready: [], building: [], submitted: [], none: [] };
    for (const order of rows) {
      const folder = folderByOrder.get(order.id);
      out[groupOf(folder)].push({ key: `order:${order.id}`, order, folder, date: folder?.embargo || order.expectedAvailability || '9999' });
    }
    for (const folder of unmatchedFolders) {
      out[groupOf(folder)].push({ key: `folder:${folder.folder}`, folder, date: folder.embargo || '9999' });
    }
    for (const list of Object.values(out)) list.sort((a, b) => a.date.localeCompare(b.date) || b.key.localeCompare(a.key));
    return out;
  }, [rows, unmatchedFolders, folderByOrder]);

  if (rows.length === 0 && (folders?.length ?? 0) === 0) {
    return <WorkEmpty>Everything announced or ordered is set up in the catalog.</WorkEmpty>;
  }

  const renderRow = (order: OrderSummary) => {
    const c = order.counts;
    // new_product = confirmed missing from the catalog; unresolved+no-pid =
    // never checked against it. Both are setup work, so the badge carries the
    // sum and the hint breaks the mix down.
    const setupProducts = (c.newProductLines || 0) + (c.unresolvedLines || 0);
    const setupParts = setupBreakdown(c).map((p) => p.text);
    const pending = verifyTaskByOrder.get(order.id);
    const overdue = order.expectedAvailability != null
      && order.expectedAvailability < todayDateString();
    const folder = folderByOrder.get(order.id);
    const folderExpanded = folder != null && openFolders.has(folder.folder);
    return (
      <WorkRow
        key={order.id}
        scrollKey={`order:${order.id}`}
        title={<JourneyName vendor={order.vendor} label={order.label} onOpen={() => onOpenJourney(order.id)} />}
        detail={folderExpanded && folder ? <SetupFolderPanel folder={folder} /> : undefined}
        actions={
          <>
            {pending ? (
              <PendingNudge task={pending} onWithdrawn={onChanged} />
            ) : (
              <Can code="orders:write:nudges">
                <Hint content="Records these as set up; the next run re-checks the catalog.">
                  <RowAction
                    busy={busyId === order.id}
                    disabled={busyId === order.id}
                    onClick={async () => {
                      setBusyId(order.id);
                      try {
                        await createOrderTask(order.id, { kind: 'verify_setup' });
                        onChanged();
                      } finally {
                        setBusyId(null);
                      }
                    }}
                  >
                    Mark set up
                  </RowAction>
                </Hint>
              </Can>
            )}
          </>
        }
      >
        {order.needsSetup && (
          <Hint content={setupParts.length > 1 ? setupParts.join(', plus ') : undefined}>
            <span className={statusPillClass('warning')}>
              <Wrench className="h-3 w-3" />
              {setupProducts > 0
                ? `${setupProducts} product${setupProducts === 1 ? '' : 's'} not set up`
                : `${c.releaseNamesMissing} release${c.releaseNamesMissing === 1 ? '' : 's'} not set up`}
            </span>
          </Hint>
        )}
        {order.newSupplierNeeded && (
          <NewSupplierLink vendor={order.vendor} className="text-xs" />
        )}

        {folder && (
          <SetupFolderChip
            folder={folder}
            expanded={folderExpanded}
            onToggle={() => toggleFolder(folder.folder)}
          />
        )}

        {order.expectedAvailability && (
          <Hint content={overdue ? 'The vendor says this already shipped.' : 'Vendor ship date.'}>
            <span className={statusPillClass(dueTone(order.expectedAvailability) ?? 'neutral')}>
              ships {fmtDate(order.expectedAvailability)}
            </span>
          </Hint>
        )}
      </WorkRow>
    );
  };

  const renderFolderRow = (f: SetupFolderSummary) => {
    const expanded = openFolders.has(f.folder);
    return (
      <WorkRow
        key={f.folder}
        scrollKey={`folder:${f.folder}`}
        title={
          <JourneyName
            vendor={f.vendor ?? f.folder}
            label={null}
            onOpen={f.orderId ? () => onOpenJourney(f.orderId!) : undefined}
          />
        }
        detail={expanded ? <SetupFolderPanel folder={f} /> : undefined}
      >
        <SetupFolderChip
          folder={f}
          expanded={expanded}
          onToggle={() => toggleFolder(f.folder)}
        />
        {f.embargo && (
          <span className={statusPillClass(dueTone(f.embargo) ?? 'neutral')}>street {fmtDate(f.embargo)}</span>
        )}
      </WorkRow>
    );
  };

  return (
    <div>
      {foldersError && (
        <WorkNotice tone="danger" onRetry={() => void loadFolders()}>
          Setup folders failed to load: {foldersError}
        </WorkNotice>
      )}

      {(Object.keys(GROUP_TITLES) as SetupGroup[]).map((g) => groups[g].length > 0 && (
        <WorkSection key={g} title={GROUP_TITLES[g]} count={groups[g].length}>
          {groups[g].map((item) => (item.order ? renderRow(item.order) : renderFolderRow(item.folder!)))}
        </WorkSection>
      ))}

      {parkedFolders.length > 0 && (
        <div className="py-3">
          <RowAction variant="quiet" onClick={() => setShowParked((v) => !v)}>
            {showParked ? 'Hide parked' : `Show parked (${parkedFolders.length})`}
          </RowAction>
        </div>
      )}
      {showParked && parkedFolders.length > 0 && (
        <WorkSection title="Parked" count={parkedFolders.length}>
          {parkedFolders.map(renderFolderRow)}
        </WorkSection>
      )}
    </div>
  );
}
